import LegalLayout, { LegalSection } from "@/components/LegalLayout";
import { SUPPORT_EMAIL } from "@/lib/assets";

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      testid="privacy-policy-page"
      title="Privacy Policy"
      updated="June 2026"
    >
      <p>
        This Privacy Policy explains how Waidbook ("we", "us", or "our")
        collects, uses, and protects your information when you use the Waidbook
        mobile application and this website. By using Waidbook, you agree to the
        practices described below.
      </p>

      <LegalSection heading="1. Information We Collect">
        <p>We collect the following types of information:</p>
        <ul className="list-disc space-y-2 pl-6 marker:text-[#CAA362]">
          <li>
            <strong className="text-white/90">Account information</strong> —
            your name, email address, and password when you create an account.
          </li>
          <li>
            <strong className="text-white/90">Hunting log data</strong> —
            harvest records, notes, photos, dates, and species you choose to
            store.
          </li>
          <li>
            <strong className="text-white/90">Location data</strong> — GPS
            coordinates for stands and markers, used only while you use the app
            and only with your permission.
          </li>
          <li>
            <strong className="text-white/90">Device information</strong> —
            basic technical data such as device type and app version, used to
            keep the service running reliably.
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="2. How We Use Your Information">
        <p>We use your information solely to provide and improve Waidbook:</p>
        <ul className="list-disc space-y-2 pl-6 marker:text-[#CAA362]">
          <li>To create and manage your account.</li>
          <li>To store and display your hunting logs, markers, and photos.</li>
          <li>To provide weather, moon phase, and mapping features.</li>
          <li>To respond to support requests and communicate with you.</li>
        </ul>
        <p>
          We do not sell your personal data, and we do not use your hunting logs
          for advertising.
        </p>
      </LegalSection>

      <LegalSection heading="3. Data Storage & Security">
        <p>
          Your data is stored securely on protected servers. We use industry
          standard measures to safeguard your information against unauthorized
          access, alteration, or disclosure. While no method of transmission is
          100% secure, we work hard to protect your data.
        </p>
      </LegalSection>

      <LegalSection heading="4. Location Permissions">
        <p>
          Waidbook requests access to your device location to place stands,
          markers, and to provide accurate maps. You can enable or disable
          location access at any time in your device settings. The app remains
          usable without location, though some features will be limited.
        </p>
      </LegalSection>

      <LegalSection heading="5. Sharing of Information">
        <p>
          We only share information with trusted service providers who help us
          operate the app (for example, hosting and mapping services), and only
          to the extent necessary. We may disclose information if required by
          law.
        </p>
      </LegalSection>

      <LegalSection heading="6. Your Rights">
        <p>
          You have the right to access, correct, or delete your personal data.
          You can delete your account and all associated data directly from
          within the app — see our{" "}
          <a
            href="/delete-account"
            className="text-[#E0BE82] underline underline-offset-4 hover:text-[#CAA362]"
          >
            Delete Account
          </a>{" "}
          page for step-by-step instructions.
        </p>
      </LegalSection>

      <LegalSection heading="7. Children's Privacy">
        <p>
          Waidbook is not intended for children under the age of 16. We do not
          knowingly collect personal information from children.
        </p>
      </LegalSection>

      <LegalSection heading="8. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. Any changes will
          be posted on this page with an updated revision date.
        </p>
      </LegalSection>

      <LegalSection heading="9. Contact Us">
        <p>
          If you have any questions about this Privacy Policy or your data,
          please contact us at{" "}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="text-[#E0BE82] underline underline-offset-4 hover:text-[#CAA362]"
          >
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
